import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";

import { TouchpointStatusDictionaryItem } from "hooks/api/dictionary";

import { getStatuses } from "components/partials/selected-items/selected-items.helper";
import { buildTouchpointPreviewHref, getTouchpointPaths } from "utilities/app-routes";

import { TouchpointItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { TouchpointStatuses } from "types";
import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";

import Icons from "assets";
import { IconButton } from "ui/icon-button";
import { Chip } from "ui/chip";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { getDisplayTouchpointIcon, getDisplayTouchpointType } from "utilities";

interface TableTouchpointsProps {
  clientId: string;
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
  hasTemplates: boolean;
  /** Client approver: adjust column header and "feedback received" phrasing in the status column. */
  isClientAdmin?: boolean;
}

const headerStyles = "text-gray-600 font-bold leading-5 px-4 py-3";

export const createBatchTouchpointsTableColumns = ({
  clientId,
  touchpointVersionStatuses,
  hasTemplates,
  isClientAdmin = false,
}: TableTouchpointsProps): ColumnDef<TouchpointItem>[] => {
  return [
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      enableSorting: true,
      cell: ({ row }) => {
        const touchpointType = row.original.type as TouchpointType;
        const isTemplate = row.original.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
        const icon = getDisplayTouchpointIcon(touchpointType);
        const title =
          touchpointType === TouchpointType.OTHER
            ? TouchpointTypeLabel.OTHER
            : getDisplayTouchpointType(touchpointType);

        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className="size-4 mt-0.5">{icon}</div>
            <span className="text-gray-800 font-medium">{title}</span>
            {isTemplate && <Chip variant="filled" size="small" label="Template" />}
          </div>
        );
      },
      size: hasTemplates ? 160 : 120,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "touchpointName",
      header: "Creative Name",
      accessorKey: "touchpointName",
      enableSorting: true,
      cell: ({ row }) => {
        const link = buildTouchpointPreviewHref(
          getTouchpointPaths({
            clientId,
            campaignId: row.original.campaignId,
            touchpointId: row.original.touchpointId,
          }).touchpointPreviewPath,
          row.original.id,
        );
        const status = getStatuses(row.original.status, touchpointVersionStatuses);

        return (
          <div className="flex flex-col justify-center">
            <Link
              to={link}
              className="text-aqua-600 hover:text-aqua-700 font-semibold line-clamp-1"
              aria-disabled={false}
              tabIndex={undefined}>
              {row.original.touchpointName}
            </Link>
            <span className="text-gray-500 text-sm">
              v {row.original.version} ({status?.versionStatusName})
            </span>
          </div>
        );
      },
      size: 250,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "campaignName",
      header: "Campaign Name",
      accessorKey: "campaignName",
      enableSorting: true,
      cell: ({ row }) => {
        return (
          <span className="text-gray-600 font-medium line-clamp-1">
            {row.original.campaignName}
          </span>
        );
      },
      size: 200,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "status",
      header: isClientAdmin ? "Status" : "Client review status",
      accessorKey: "status",
      enableSorting: true,
      cell: ({ row }) => {
        const status = getStatuses(row.original.status, touchpointVersionStatuses);
        const isFeedbackReceivedState =
          row.original.status === TouchpointStatuses.REJECTED ||
          row.original.status === TouchpointStatuses.SYSTEM_DRAFT;
        const versionStatusesForDisplay =
          isClientAdmin && status && isFeedbackReceivedState
            ? { ...status, clientReviewStatusName: "Feedback sent" }
            : status;

        return <StatusIndicator versionStatuses={versionStatusesForDisplay} />;
      },
      size: 140,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "comments",
      header: "Comments",
      accessorKey: "comments",
      enableSorting: false,
      cell: ({ row }) => {
        return <div className="text-gray-600 text-center">{row.original.openCommentCount}</div>;
      },
      size: 150,
      meta: {
        isHeaderCentered: true,
        headerClassName: headerStyles,
      },
    },
    {
      id: "action",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => {
        const viewLink = buildTouchpointPreviewHref(
          getTouchpointPaths({
            clientId,
            campaignId: row.original.campaignId,
            touchpointId: row.original.touchpointId,
          }).touchpointPreviewPath,
          row.original.id,
        );

        return (
          <div className="flex items-center justify-center gap-5">
            <IconButton
              to={viewLink}
              aria-label="View creative"
              data-testid="view-touchpoint-button"
              size="medium"
              variant="text">
              <Icons.EyeVisible />
            </IconButton>
          </div>
        );
      },
      size: 56,
      meta: {
        headerClassName: headerStyles,
        className: "py-1 pr-8",
        isHeaderCentered: true,
      },
    },
  ];
};
